import { reportingCalendarYear, viewerReportingYearOptions } from '../../../shared/utils/reportingYear';

const MONTH_KEYS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

const currentYear = reportingCalendarYear();
export const EMISSION_SCREEN_YEARS = [
  currentYear + 1,
  ...viewerReportingYearOptions(),
];

const CATEGORY_LABELS = {
  cat1: 'scopeEmissions.scope3.category.cat1',
  cat2: 'scopeEmissions.scope3.category.cat2',
  cat3: 'scopeEmissions.scope3.category.cat3',
  cat5: 'scopeEmissions.scope3.category.cat5',
  cat6: 'scopeEmissions.scope3.category.cat6',
  cat7: 'scopeEmissions.scope3.category.cat7',
  cat8: 'scopeEmissions.scope3.category.cat8',
};

const CATEGORY_IDS = ['cat1', 'cat2', 'cat3', 'cat5', 'cat6', 'cat7', 'cat8'];
const CATEGORY_CODES = Object.fromEntries(
  CATEGORY_IDS.map((id) => [id, `Cat ${id.replace('cat', '')}`]),
);

const SUBCATEGORY_IDS = Array.from({ length: 9 }, (_, index) => `cat1_${index + 1}`);

const SUBCATEGORY_LABELS = Object.fromEntries(
  SUBCATEGORY_IDS.map((id) => {
    return [id, `scopeEmissions.scope3.subcategory.${id}`];
  }),
);

const DISTRIBUTION_LABELS = {
  scope3Cat1: ['dashboard.distribution.cat1', 'dashboard.distribution.cat1Short'],
  scope3Cat2: ['dashboard.distribution.cat2', 'dashboard.distribution.cat2Short'],
  scope3Cat3: ['dashboard.distribution.cat3', 'dashboard.distribution.cat3Short'],
  scope3Cat5: ['dashboard.distribution.cat5', 'dashboard.distribution.cat5Short'],
  scope3Cat6: ['dashboard.distribution.cat6', 'dashboard.distribution.cat6Short'],
  scope3Cat7: ['dashboard.distribution.cat7', 'dashboard.distribution.cat7Short'],
  scope3Cat8: ['dashboard.distribution.cat8', 'dashboard.distribution.cat8Short'],
  scope2: ['dashboard.distribution.scope2', 'dashboard.distribution.scope2Short'],
  scope1: ['dashboard.distribution.scope1', 'dashboard.distribution.scope1Short'],
  scope3: ['dashboard.distribution.scope3', 'dashboard.distribution.scope3Short'],
};

const WARNING_ANOMALIES = new Set([
  'CALCULATION_STALE',
  'MISSING_CAT5_SEAT_SNAPSHOT',
  'MISSING_CAT5_SEAT_Q4_BASELINE',
  'MISSING_CAT8_Q4_BASELINE',
  'INVALID_SCOPE',
  'SCOPE1_MULTIPLE_FACTORS',
  'SCOPE2_MULTIPLE_FACTORS',
  'MISSING_ACTIVITY_PERIOD',
]);

const ANOMALY_LABELS = {
  CALCULATION_STALE: 'emissionOverview.anomalies.calculationStale',
  MISSING_CAT5_SEAT_SNAPSHOT: 'emissionOverview.anomalies.missingCat5SeatSnapshot',
  MISSING_CAT5_SEAT_Q4_BASELINE: 'emissionOverview.anomalies.missingCat5SeatSnapshot',
  MISSING_CAT8_Q4_BASELINE: 'emissionOverview.anomalies.missingCat8Q4Baseline',
  SCOPE1_MULTIPLE_FACTORS: 'emissionOverview.anomalies.scope1MultipleFactors',
  SCOPE2_MULTIPLE_FACTORS: 'emissionOverview.anomalies.scope2MultipleFactors',
  MISSING_ACTIVITY_PERIOD: 'emissionOverview.anomalies.missingActivityPeriod',
  INVALID_SCOPE: 'emissionOverview.anomalies.invalidScope',
  SCOPE1_BREAKDOWN_RECONCILIATION_FAILED: 'emissionOverview.anomalies.scope1Reconciliation',
  SCOPE2_BREAKDOWN_RECONCILIATION_FAILED: 'emissionOverview.anomalies.scope2Reconciliation',
  SCOPE3_CATEGORY_RECONCILIATION_FAILED: 'emissionOverview.anomalies.scope3CategoryReconciliation',
  SCOPE3_MONTHLY_RECONCILIATION_FAILED: 'emissionOverview.anomalies.scope3MonthlyReconciliation',
  SCOPE3_ANNUAL_UNALLOCATED_RECONCILIATION_FAILED: 'emissionOverview.anomalies.scope3AnnualReconciliation',
};

export function describeOverviewAnomaly(anomaly) {
  const [code, ...targetParts] = String(anomaly || '').split(':');
  return {
    code,
    target: targetParts.join(':'),
    blocking: !WARNING_ANOMALIES.has(code),
    userVisible: Boolean(ANOMALY_LABELS[code]),
    labelKey: ANOMALY_LABELS[code] || 'emissionOverview.anomalies.unknown',
    raw: String(anomaly || ''),
  };
}

const toNumber = (value) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : 0;
};

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const asRecord = value => (isRecord(value) ? value : {});
const asArray = value => (Array.isArray(value) ? value : []);

const normalizeEstimatedUsage = (usage = {}) => {
  const safeUsage = asRecord(usage);
  return {
    estimatedRecords: toNumber(safeUsage.estimated_records),
    sourceYears: [...new Set(asArray(safeUsage.source_years)
      .map(Number)
      .filter(Number.isFinite))].sort((a, b) => b - a),
  };
};

const combineEstimatedUsage = (...items) => ({
  estimatedRecords: items.reduce((total, item) => total + item.estimatedRecords, 0),
  sourceYears: [...new Set(items.flatMap((item) => item.sourceYears))].sort((a, b) => b - a),
});

const monthKey = (month) => MONTH_KEYS[Number(month) - 1] || MONTH_KEYS[0];

const emptyMonths = () => MONTH_KEYS.map((key) => ({ monthKey: key, value: 0 }));
const emptyDashboardMonths = () => MONTH_KEYS.map(key => ({
  monthKey: key, scope1: 0, scope2: 0, scope3: 0, total: 0,
}));
const emptyScope1Months = () => MONTH_KEYS.map(key => ({
  monthKey: key, emissions: 0, volumeLitres: 0,
}));
const emptyDashboardYears = years => [...years].reverse().map(year => ({
  year,
  scope1: 0,
  scope2: 0,
  scope3: 0,
  total: 0,
  source: 'UNKNOWN',
}));

const normalizeMetrics = (metrics = {}) => {
  const safeMetrics = asRecord(metrics);
  return {
    total: toNumber(safeMetrics.total),
    scope1: toNumber(safeMetrics.scope_1),
    scope2: toNumber(safeMetrics.scope_2),
    scope3: toNumber(safeMetrics.scope_3),
  };
};

const historicalDetailsFor = (payload, scope) => asArray(payload?.details)
  .filter(item => item?.scope === scope);

const historicalTotalFor = (payload, scope) => toNumber(payload?.scope_totals?.[scope]);

const historicalMeta = (payload, year, key) => ({
  year,
  years: EMISSION_SCREEN_YEARS,
  hasData: true,
  isHistorical: true,
  readiness: { [key]: false },
  status: { [key]: 'HISTORICAL' },
  estimatedFactors: { [key]: { estimatedRecords: 0, sourceYears: [] } },
  anomalies: [],
});

const historicalLabel = (item) => {
  if (item?.aggregation_level === 'COMPANY_TOTAL') {
    return item.source_label || item.activity_name || 'Historical detail';
  }
  return item?.facility_name
    || item?.legal_entity_name
    || item?.activity_name
    || item?.source_label
    || item?.category_label
    || item?.category_code
    || 'Historical detail';
};

const historicalRowId = (item, scope, index) => [
  scope,
  item?.subcategory_code || item?.category_code || item?.source_code || 'row',
  index,
].join('-');

const historicalCategoryIdFor = (code, fallback = 'unknown') => {
  const normalized = String(code || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const match = normalized.match(/cat([1-8])/);
  return match ? `cat${match[1]}` : fallback;
};

const historicalSubcategoryIdFor = (code, fallback = 'unknown') => {
  const normalized = String(code || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const match = normalized.match(/(?:cat)?1([1-9])$/);
  return match ? `cat1_${match[1]}` : fallback;
};

const historicalCategoryId = item => {
  const rawCode = String(item.category_code || item.category_label || 'other').toLowerCase();
  const match = rawCode.match(/cat[_\s-]*(\d+)/);
  return match ? `cat${match[1]}` : rawCode.replace(/[^a-z0-9]+/g, '');
};

const historicalDashboardDistribution = (report, metrics) => {
  const breakdown = asArray(report?.scope_3_breakdown)
    .filter(item => item?.status === 'CALCULATED');
  const details = historicalDetailsFor(report, 'SCOPE_3');
  const categories = breakdown.length
    ? mergeCategories(breakdown.map(item => ({
      id: historicalCategoryIdFor(item.category_code),
      code: item.category_code,
      value: item.co2_tonnes,
    })))
    : mergeCategories([...details.reduce((byId, item) => {
      const id = historicalCategoryId(item);
      const current = byId.get(id) || {
        id, code: item.category_code || id, value: 0,
      };
      current.value += toNumber(item.co2_tonnes);
      byId.set(id, current);
      return byId;
    }, new Map()).values()]);
  if (categories.length === 0 && metrics.scope3 > 0) {
    categories.push(normalizeCategory({ id: 'scope3', code: 'Scope 3', value: metrics.scope3 }));
  }

  return [
    ...categories
      .filter(item => item.value > 0)
      .map(item => ({
        id: item.id === 'scope3' ? 'scope3' : `scope3${item.id[0].toUpperCase()}${item.id.slice(1)}`,
        labelKey: DISTRIBUTION_LABELS[item.id === 'scope3' ? 'scope3' : `scope3${item.id[0].toUpperCase()}${item.id.slice(1)}`]?.[0]
          || item.code,
        shortLabelKey: DISTRIBUTION_LABELS[item.id === 'scope3' ? 'scope3' : `scope3${item.id[0].toUpperCase()}${item.id.slice(1)}`]?.[1]
          || item.code,
        value: item.value,
      })),
    { id: 'scope2', labelKey: DISTRIBUTION_LABELS.scope2[0], shortLabelKey: DISTRIBUTION_LABELS.scope2[1], value: metrics.scope2 },
    { id: 'scope1', labelKey: DISTRIBUTION_LABELS.scope1[0], shortLabelKey: DISTRIBUTION_LABELS.scope1[1], value: metrics.scope1 },
  ].filter(item => item.value > 0);
};

export function normalizeHistoricalDashboard(reports = [], requestedYear) {
  const reportList = asArray(reports).filter(isRecord);
  const report = reportList.find(item => Number(item.fiscal_year) === Number(requestedYear));
  const previousYear = Number(requestedYear) - 1;
  const previousReport = reportList.find(item => Number(item.fiscal_year) === previousYear);
  const yearly = reportList.map(item => ({
    year: Number(item.fiscal_year),
    total: toNumber(item.scope_totals?.total),
  })).filter(item => Number.isFinite(item.year));
  if (!report) {
    const empty = createEmptyDashboard(requestedYear);
    return {
      ...empty,
      meta: { ...empty.meta, isHistorical: true },
      dashboard: { ...empty.dashboard, monthly: [] },
    };
  }
  const metrics = {
    total: toNumber(report.scope_totals?.total),
    scope1: historicalTotalFor(report, 'scope_1'),
    scope2: historicalTotalFor(report, 'scope_2'),
    scope3: historicalTotalFor(report, 'scope_3'),
  };
  const previousMetrics = previousReport ? {
    total: toNumber(previousReport.scope_totals?.total),
    scope1: historicalTotalFor(previousReport, 'scope_1'),
    scope2: historicalTotalFor(previousReport, 'scope_2'),
    scope3: historicalTotalFor(previousReport, 'scope_3'),
  } : null;
  return {
    meta: {
      ...historicalMeta(report, requestedYear, 'dashboard'),
      isPlaceholderData: Boolean(report.is_placeholder_data),
    },
    dashboard: {
      year: requestedYear,
      metrics,
      previousMetrics,
      previousYear: previousReport ? previousYear : null,
      monthly: [],
      distribution: historicalDashboardDistribution(report, metrics),
      yearly,
    },
  };
}

export function normalizeHistoricalScope1(report, requestedYear) {
  const details = historicalDetailsFor(report, 'SCOPE_1');
  return {
    meta: { ...historicalMeta(report, requestedYear, 'scope1'), isPlaceholderData: Boolean(report?.is_placeholder_data) },
    scope1: {
      total: historicalTotalFor(report, 'scope_1'),
      monthly: [],
      companies: details.map((item, index) => ({
        id: historicalRowId(item, 'scope1', index),
        companyCode: '',
        companyNameEn: historicalLabel(item),
        legalEntity: historicalLabel(item),
        volumeLitres: 0,
        emissionFactor: null,
        emissions: toNumber(item.co2_tonnes),
      })),
    },
  };
}

export function normalizeHistoricalScope2(report, requestedYear) {
  const details = historicalDetailsFor(report, 'SCOPE_2');
  return {
    meta: { ...historicalMeta(report, requestedYear, 'scope2'), isPlaceholderData: Boolean(report?.is_placeholder_data) },
    scope2: {
      total: historicalTotalFor(report, 'scope_2'),
      totalKwh: 0,
      monthly: [],
      offices: details.map((item, index) => ({
        id: historicalRowId(item, 'scope2', index),
        name: historicalLabel(item),
        companyNameEn: '',
        legalEntity: '',
        legalEntityCode: '',
        kwh: 0,
        emissionFactor: null,
        factorStatus: null,
        renewable: false,
        sourceYear: null,
        isEstimated: false,
        applicationMode: null,
        registrationNo: null,
        itemKey: null,
        assignmentVersion: null,
        emissions: toNumber(item.co2_tonnes),
      })),
    },
  };
}

export function normalizeHistoricalScope3(report, requestedYear) {
  const details = historicalDetailsFor(report, 'SCOPE_3');
  const breakdown = asArray(report?.scope_3_breakdown);
  const calculatedBreakdown = breakdown.filter(item => item?.status === 'CALCULATED');
  const categories = calculatedBreakdown.length
    ? mergeCategories(calculatedBreakdown.map(item => ({
      id: historicalCategoryIdFor(item.category_code),
      code: item.category_code,
      value: item.co2_tonnes,
    })))
    : (() => {
      const valuesByCategory = new Map();
      details.forEach(item => {
        const id = historicalCategoryId(item);
        valuesByCategory.set(id, (valuesByCategory.get(id) || 0) + toNumber(item.co2_tonnes));
      });
      return [...valuesByCategory.entries()].map(([id, value]) => normalizeCategory({ id, code: id, value }));
    })();
  const cat1Breakdown = calculatedBreakdown.find(
    item => historicalCategoryIdFor(item.category_code) === 'cat1',
  );
  const cat1 = categories.find(item => item.id === 'cat1')
    || normalizeCategory({ id: 'cat1', value: 0 }, false);
  const subcategories = cat1Breakdown?.subcategory_breakdown_available
    ? mergeCat1Subcategories((cat1Breakdown.subcategories || [])
      .filter(item => item?.status === 'CALCULATED')
      .map(item => ({
        id: historicalSubcategoryIdFor(item.subcategory_code),
        code: item.subcategory_code,
        value: item.co2_tonnes,
      })))
    : [];
  const total = historicalTotalFor(report, 'scope_3');
  const overallTotal = historicalTotalFor(report, 'total');
  return {
    meta: { ...historicalMeta(report, requestedYear, 'scope3'), isPlaceholderData: Boolean(report?.is_placeholder_data) },
    scope3: {
      total: historicalTotalFor(report, 'scope_3'),
      shareOfTotal: overallTotal > 0 ? (total / overallTotal) * 100 : 0,
      monthly: [],
      categories: categories.length ? categories : mergeCategories(),
      cat1: { ...cat1, subcategories },
    },
  };
}

const normalizeCategory = (category = {}, hasData = true) => ({
  id: category.id || 'unknown',
  code: category.code || category.id || '',
  labelKey: CATEGORY_LABELS[category.id] || category.code || category.id || '',
  value: toNumber(category.value),
  hasData,
});

const normalizeSubcategory = (category = {}, hasData = true) => ({
  id: category.id || 'unknown',
  code: category.code || category.id || '',
  labelKey: SUBCATEGORY_LABELS[category.id] || category.code || category.id || '',
  value: toNumber(category.value),
  hasData,
});

const mergeCategories = (items = []) => {
  const normalized = items.map((item) => normalizeCategory(item));
  const byId = new Map(normalized.map((item) => [item.id, item]));
  const canonical = CATEGORY_IDS.map((id) => (
    byId.get(id) || normalizeCategory({ id, code: CATEGORY_CODES[id], value: 0 }, false)
  ));
  return [...canonical, ...normalized.filter((item) => !CATEGORY_IDS.includes(item.id))];
};

const mergeCat1Subcategories = (items = []) => {
  const normalized = items.map((item) => normalizeSubcategory(item));
  const byId = new Map(normalized.map((item) => [item.id, item]));
  const canonical = SUBCATEGORY_IDS.map((id, index) => (
    byId.get(id) || normalizeSubcategory({ id, code: `1.${index + 1}`, value: 0 }, false)
  ));
  return [...canonical, ...normalized.filter((item) => !SUBCATEGORY_IDS.includes(item.id))];
};

const normalizeYears = (meta = {}) => {
  const safeMeta = asRecord(meta);
  const years = asArray(safeMeta.years)
    .filter(isRecord)
    .map(item => Number(item.fiscal_year))
    .filter(Number.isFinite);
  return [...new Set([...EMISSION_SCREEN_YEARS, ...years])]
    .sort((left, right) => right - left);
};

const baseMeta = (fiscalYear, key) => ({
  year: fiscalYear,
  years: EMISSION_SCREEN_YEARS,
  hasData: false,
  readiness: { [key]: false },
  status: { [key]: 'EMPTY' },
  estimatedFactors: { [key]: { estimatedRecords: 0, sourceYears: [] } },
  anomalies: [],
});

const normalizeScopeMeta = (meta = {}, key) => {
  const safeMeta = asRecord(meta);
  const fiscalYear = Number(safeMeta.fiscal_year) || currentYear;
  return {
    year: fiscalYear,
    years: normalizeYears(safeMeta),
    hasData: Boolean(safeMeta.has_data),
    readiness: { [key]: Boolean(safeMeta.readiness) },
    status: { [key]: safeMeta.status || 'EMPTY' },
    estimatedFactors: { [key]: normalizeEstimatedUsage(safeMeta.estimated_factors) },
    anomalies: asArray(safeMeta.anomalies),
  };
};

export function createEmptyDashboard(fiscalYear = currentYear) {
  const years = EMISSION_SCREEN_YEARS;
  return {
    meta: {
      ...baseMeta(fiscalYear, 'dashboard'),
      previousYear: null,
      readiness: { scope1: false, scope2: false, scope3: false },
      estimatedFactors: {
        dashboard: { estimatedRecords: 0, sourceYears: [] },
        scope1: { estimatedRecords: 0, sourceYears: [] },
        scope2: { estimatedRecords: 0, sourceYears: [] },
        scope3: { estimatedRecords: 0, sourceYears: [] },
      },
    },
    dashboard: {
      year: fiscalYear,
      metrics: { total: 0, scope1: 0, scope2: 0, scope3: 0 },
      annualUnallocated: 0,
      previousMetrics: null,
      previousYear: null,
      monthly: emptyDashboardMonths(),
      distribution: [],
      yearly: emptyDashboardYears(years),
    },
  };
}

export function normalizeDashboard(payload = {}) {
  const safePayload = asRecord(payload);
  const meta = asRecord(safePayload.meta);
  const dashboard = asRecord(safePayload.dashboard);
  const fiscalYear = Number(meta.fiscal_year) || currentYear;
  const estimated = asRecord(meta.estimated_factors);
  const scope1 = normalizeEstimatedUsage(estimated.scope_1);
  const scope2 = normalizeEstimatedUsage(estimated.scope_2);
  const scope3 = normalizeEstimatedUsage(estimated.scope_3);
  const years = normalizeYears(meta);
  const monthly = asArray(dashboard.monthly).filter(isRecord).map(item => ({
    monthKey: monthKey(item.month),
    scope1: toNumber(item.scope_1),
    scope2: toNumber(item.scope_2),
    scope3: toNumber(item.scope_3),
    total: toNumber(item.total),
  }));
  const yearly = asArray(dashboard.yearly).filter(isRecord).map(item => ({
    year: Number(item.fiscal_year),
    scope1: toNumber(item.scope_1),
    scope2: toNumber(item.scope_2),
    scope3: toNumber(item.scope_3),
    total: toNumber(item.total),
    source: item.source || 'UNKNOWN',
  }));
  const distribution = asArray(dashboard.distribution).filter(isRecord).map((item) => {
    const labels = DISTRIBUTION_LABELS[item.id] || [item.code, item.code];
    return {
      id: item.id,
      labelKey: labels[0],
      shortLabelKey: labels[1],
      value: toNumber(item.value),
    };
  });
  return {
    meta: {
      year: fiscalYear,
      years,
      hasData: Boolean(meta.has_data),
      previousYear: meta.previous_year ?? null,
      readiness: {
        scope1: Boolean(asRecord(meta.readiness).scope_1),
        scope2: Boolean(asRecord(meta.readiness).scope_2),
        scope3: Boolean(asRecord(meta.readiness).scope_3),
      },
      status: { dashboard: meta.status || 'EMPTY' },
      estimatedFactors: {
        dashboard: combineEstimatedUsage(scope1, scope2, scope3),
        scope1,
        scope2,
        scope3,
      },
      anomalies: asArray(meta.anomalies),
    },
    dashboard: {
      year: fiscalYear,
      metrics: normalizeMetrics(dashboard.metrics),
      previousMetrics: dashboard.previous_metrics
        ? normalizeMetrics(dashboard.previous_metrics)
        : null,
      previousYear: meta.previous_year ?? null,
      monthly: monthly.length ? monthly : emptyDashboardMonths(),
      distribution,
      yearly: yearly.length ? yearly : emptyDashboardYears(years),
      annualUnallocated: toNumber(dashboard.annual_unallocated),
    },
  };
}

export function createEmptyScope1(fiscalYear = currentYear) {
  return {
    meta: baseMeta(fiscalYear, 'scope1'),
    scope1: {
      total: 0,
      monthly: emptyScope1Months(),
      companies: [],
    },
  };
}

export function normalizeScope1(payload = {}) {
  const safePayload = asRecord(payload);
  const scope = asRecord(safePayload.scope_1);
  const monthly = asArray(scope.monthly).filter(isRecord).map(item => ({
    monthKey: monthKey(item.month),
    emissions: toNumber(item.value),
    volumeLitres: toNumber(item.volume_litres),
  }));
  return {
    meta: normalizeScopeMeta(safePayload.meta, 'scope1'),
    scope1: {
      total: toNumber(scope.total),
      monthly: monthly.length ? monthly : emptyScope1Months(),
      companies: asArray(scope.companies).filter(isRecord).map(company => ({
        id: company.id,
        companyCode: company.company_code,
        companyNameEn: company.company_name_en || '',
        legalEntity: company.legal_entity || company.company_code || '',
        volumeLitres: toNumber(company.volume_litres),
        emissionFactor: company.emission_factor === null || company.emission_factor === undefined
          ? null
          : toNumber(company.emission_factor),
        emissions: toNumber(company.emissions),
      })),
    },
  };
}

export function createEmptyScope2(fiscalYear = currentYear) {
  return {
    meta: baseMeta(fiscalYear, 'scope2'),
    scope2: {
      total: 0,
      totalKwh: 0,
      monthly: MONTH_KEYS.map(key => ({ monthKey: key, emissions: 0, kwh: 0 })),
      offices: [],
    },
  };
}

export function normalizeScope2(payload = {}) {
  const safePayload = asRecord(payload);
  const scope = asRecord(safePayload.scope_2);
  const monthRows = asArray(scope.monthly)
    .filter(isRecord)
    .filter((item) => {
      const month = Number(item?.month);
      return Number.isInteger(month) && month >= 1 && month <= MONTH_KEYS.length;
    });
  const monthsByNumber = new Map(monthRows.map((item) => {
    const month = Number(item.month);
    return [month, {
      monthKey: MONTH_KEYS[month - 1],
      kwh: toNumber(item.value ?? item.kwh),
      emissions: toNumber(item.emissions),
    }];
  }));

  return {
    meta: normalizeScopeMeta(safePayload.meta, 'scope2'),
    scope2: {
      total: toNumber(scope.total),
      totalKwh: toNumber(scope.total_kwh),
      monthly: MONTH_KEYS.map((key, index) => (
        monthsByNumber.get(index + 1) || { monthKey: key, emissions: 0, kwh: 0 }
      )),
      offices: asArray(scope.locations).filter(isRecord).map((office) => {
        const name = String(office.name || '');
        const legalEntity = String(
          office.company_name_en || office.legal_entity || office.legal_entity_code || '',
        );
        const rawFactor = office.emission_factor;

        return {
          id: office.id,
          name,
          companyNameEn: legalEntity,
          legalEntity,
          legalEntityCode: String(office.legal_entity_code || ''),
          kwh: toNumber(office.kwh),
          emissionFactor: rawFactor === null || rawFactor === undefined || rawFactor === ''
            ? null
            : toNumber(rawFactor),
          factorStatus: office.factor_status || null,
          renewable: Boolean(office.renewable),
          sourceYear: Number(office.ef_source_year || office.source_year) || null,
          isEstimated: Boolean(office.is_estimated || office.is_carried_forward),
          applicationMode: office.application_mode || null,
          registrationNo: office.supplier_registration_no
            || office.registration_no
            || office.electricity_assignment_snapshot?.supplier_registration_no
            || null,
          itemKey: office.ef_item_key || office.item_key || null,
assignmentVersion: Number(
            office.assignment_version
              || office.electricity_assignment_snapshot?.entity_version,
          ) || null,
          emissions: toNumber(office.emissions),
        };
      }),
    },
  };
}

export function createEmptyScope3(fiscalYear = currentYear) {
  return {
    meta: baseMeta(fiscalYear, 'scope3'),
    scope3: {
      total: 0,
      shareOfTotal: 0,
      monthly: emptyMonths(),
      categories: mergeCategories(),
      cat1: {
        id: 'cat1',
        code: 'Cat 1',
        labelKey: CATEGORY_LABELS.cat1,
        value: 0,
        hasData: false,
        subcategories: mergeCat1Subcategories(),
      },
    },
  };
}

export function normalizeScope3(payload = {}) {
  const safePayload = asRecord(payload);
  const scopePayload = asRecord(safePayload.scope_3);
  const categories = mergeCategories(asArray(scopePayload.categories).filter(isRecord));
  const cat1Payload = asRecord(scopePayload.cat_1);
  const cat1Category = categories.find(category => category.id === 'cat1');
  const subcategories = mergeCat1Subcategories(
    asArray(cat1Payload.subcategories).filter(isRecord),
  );
  const monthlyByKey = new Map(
    asArray(scopePayload.monthly)
      .filter(isRecord)
      .filter((item) => {
        const month = Number(item.month);
        return Number.isInteger(month) && month >= 1 && month <= MONTH_KEYS.length;
      })
      .map((item) => [
        monthKey(item.month),
        { monthKey: monthKey(item.month), value: toNumber(item.value) },
      ]),
  );
  return {
    meta: normalizeScopeMeta(safePayload.meta, 'scope3'),
    scope3: {
      total: toNumber(scopePayload.total),
      shareOfTotal: toNumber(scopePayload.share_of_total),
      monthly: emptyMonths().map((item) => monthlyByKey.get(item.monthKey) || item),
      categories,
      cat1: {
        ...cat1Category,
        code: cat1Payload.code || cat1Category.code,
        value: cat1Payload.value === undefined
          ? cat1Category.value
          : toNumber(cat1Payload.value),
        hasData: cat1Category.hasData || subcategories.some(item => item.hasData),
        subcategories,
      },
    },
  };
}

